import { View, Platform } from 'react-native';
import Head from 'expo-router/head';
import { ReceiptText } from 'lucide-react-native';
import { usePublicThemeColors } from '@/lib/useThemeColors';
import { useTrackScreen } from '@/lib/analytics';
import { PublicLayout } from '@/components/PublicLayout';
import {
  CounselReviewBanner,
  LastUpdated,
  LegalContactCard,
  LegalLink,
  LegalParagraph,
  LegalSection,
  Strong,
} from '@/components/legal/LegalBlocks';
import { OPERATOR_ADDRESS, OPERATOR_PARTICULARS, OPERATOR_UID } from '@/lib/legal/operator';
import { REFUND_POLICY_UPDATED } from '@/lib/legal/refund-policy';

// Content mirrors the master document at docs/legal/refund-policy.md — update both together.
// src/app/__tests__/refunds-screen.test.tsx fails if a prose line of the document is missing here.

export default function RefundPolicyScreen() {
  useTrackScreen('RefundPolicy');
  const colors = usePublicThemeColors();

  return (
    <PublicLayout title="Refund Policy" titleIcon={<ReceiptText size={22} color={colors.primary[600]} />}>
      {Platform.OS === 'web' && (
        <Head>
          <title>Refund Policy — Inverio</title>
          <meta
            name="description"
            content="How cancellation, refunds, plan changes and failed payments work for paid Inverio subscriptions."
          />
          <link rel="canonical" href="https://inverio.ai/refunds" />
          <meta property="og:title" content="Refund Policy — Inverio" />
          <meta
            property="og:description"
            content="How cancellation, refunds, plan changes and failed payments work for paid Inverio subscriptions."
          />
          <meta property="og:url" content="https://inverio.ai/refunds" />
        </Head>
      )}

      <LastUpdated date={REFUND_POLICY_UPDATED} />
      <CounselReviewBanner />

      <View className="mb-6">
        <LegalParagraph>
          This Refund Policy explains how cancellation, refunds and billing changes work for paid Inverio subscriptions. It
          forms part of our <LegalLink href="/terms">Terms of Service</LegalLink> (Section 6). Nothing in it limits rights
          you have under mandatory law (Section 10).
        </LegalParagraph>
      </View>

      <LegalSection number={1} title="Who this applies to" delay={150}>
        <LegalParagraph>
          This policy applies to anyone with a paid Inverio subscription. Subscriptions are sold by{' '}
          <Strong>A. Kouadio INVARK</Strong>, a sole proprietorship registered in Switzerland (registered address: {OPERATOR_ADDRESS}; UID {OPERATOR_UID}). Payments are processed by Stripe. Prices are in Swiss francs (CHF) and are
          charged monthly or yearly, as shown before you confirm a purchase.
        </LegalParagraph>
        <LegalParagraph>
          Free plans and free beta access involve no payment, so there is nothing to refund (Section 7).
        </LegalParagraph>
      </LegalSection>

      <LegalSection number={2} title="Cancellation" delay={200}>
        <LegalParagraph>
          You can cancel at any time from your account's subscription settings, or by writing to support@inverio.ai.
          Cancelling stops the next renewal: you keep your paid features until the end of the billing period you have
          already paid for, and they end after that. You can undo a cancellation until that date.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number={3} title="Refunds" delay={250}>
        <LegalParagraph>
          Fees for a billing period that has already started are <Strong>not refunded</Strong>, in whole or in part,{' '}
          <Strong>except where the law requires it</Strong>. This applies to monthly and yearly plans alike, including when
          you cancel part-way through a period.
        </LegalParagraph>
        <LegalParagraph>
          We may make an exception at our discretion, for example if you were charged in error or a fault on our side kept
          you from using the Service. We decide each request individually (Section 9). An approved refund is returned to the
          original payment method through Stripe.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number={4} title="Plan changes" delay={300}>
        <LegalParagraph>
          Upgrades and downgrades take effect <Strong>immediately</Strong>. The price difference for the rest of the current
          billing period is calculated pro rata: an upgrade adds a prorated charge and a downgrade a prorated credit, both
          settled on your next invoice. A credit from a downgrade is applied to future invoices; it is not paid out.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number={5} title="Failed payments" delay={350}>
        <LegalParagraph>
          If a renewal payment fails, we email you, and your paid features stay available for a{' '}
          <Strong>7-day grace period</Strong> while you update your payment method. Stripe may retry the payment during that
          time. If payment does not succeed, the subscription may be cancelled and your paid features end.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number={6} title="Account deletion" delay={400}>
        <LegalParagraph>
          When you ask us to delete your account, there is a 30-day period during which you can withdraw the request.
          During that period your subscription does not renew; if you withdraw the request, the renewal setting you had
          before is restored.
        </LegalParagraph>
        <LegalParagraph>
          When the deletion is carried out, any remaining subscription is cancelled immediately and your customer record at
          Stripe is deleted. The unused part of a billing period that has already started is not refunded.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number={7} title="Beta and free access" delay={450}>
        <LegalParagraph>
          Free plans, and free or sponsored beta access, are not charged and do not renew into a paid plan. A paid plan
          starts only if you choose one and confirm its price.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number={8} title="Promotion codes" delay={500}>
        <LegalParagraph>
          You can enter a promotion code at checkout. Its discount applies as stated in the offer. Promotion codes have no
          cash value and cannot be exchanged for a refund, and a discount that has already been applied is not paid out if
          you cancel.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number={9} title="How to request a refund" delay={550}>
        <LegalParagraph>
          Email support@inverio.ai from the email address on your account, with the date of the charge and the reason for
          your request. We aim to respond within two business days.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number={10} title="Your statutory rights" delay={600}>
        <LegalParagraph>
          This policy does not limit any right you have under mandatory law. If you are a consumer in the European Union or
          the European Economic Area, you may have a statutory right to withdraw from the contract within 14 days of
          purchase. Where such a right applies to you, it takes precedence over this policy; contact support@inverio.ai to
          exercise it.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number={11} title="Changes" delay={650}>
        <LegalParagraph>
          We may update this policy. A change does not affect a billing period you have already paid for. The date above
          shows the latest revision.
        </LegalParagraph>
      </LegalSection>

      <LegalSection number={12} title="Contact" delay={700}>
        <LegalContactCard
          lines={[
            'Inverio — a service of A. Kouadio INVARK, Switzerland',
            OPERATOR_PARTICULARS,
            'Billing and refunds: support@inverio.ai',
          ]}
        />
      </LegalSection>
    </PublicLayout>
  );
}
